import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
    Button,
    Grid,

} from "@mui/material";
import { toast } from "react-toastify";

// Simple email validation regex
const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
};

const AdviceLinkPopup = ({ open, onClose, onSend, initialEmail, 
    residentName, hideContent = false, showContent=false }) => {

    const [emailData, setEmailData] = useState({
        email: initialEmail || "",
        alternateEmail: "",
        subject: "Payment Advice – Clanbridge Living Pvt. Ltd.",
        body: `Dear ${residentName || "Resident"},\n\nWe hope this message finds you well.\n\nPlease find attached the payment advice for your recent transaction with Clanbridge Living. This document contains the complete details of the payment for your reference.\n\nShould you have any questions or require further assistance, please do not hesitate to get in touch with us.\n\nThank you for choosing Clanbridge Living.\n\nBest regards,\nClanbridge Living Pvt Ltd.`,
    });
    const [errors, setErrors] = useState({
        email: false,
        alternateEmail: false,
    });

    // Update emailData when dialog opens or initialEmail/residentName changes
    useEffect(() => {
        if (open) {
            setEmailData((prev) => ({
                ...prev,
                email: initialEmail || "",
                body: `Dear ${residentName || "Resident"},\n\nWe hope this message finds you well.\n\nPlease find attached the payment advice for your recent transaction with Clanbridge Living. This document contains the complete details of the payment for your reference.\n\nShould you have any questions or require further assistance, please do not hesitate to get in touch with us.\n\nThank you for choosing Clanbridge Living.\n\nBest regards,\nClanbridge Living Pvt Ltd.`,
            }));
            setErrors({ email: false, alternateEmail: false });
        }
    }, [initialEmail, residentName, open]);

    // Handle input changes for email form fields
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setEmailData((prev) => ({
            ...prev,
            [name]: value,
        }));

        // Validate email fields on change
        if (name === "email") {
            setErrors((prev) => ({
                ...prev,
                email: value.trim() && !isValidEmail(value) ? "Invalid email format" : false,
            }));
        } else if (name === "alternateEmail") {
            // Split alternateEmail by commas and validate each email
            const emails = value.split(",").map((email) => email.trim()).filter(Boolean);
            const invalidEmails = emails.filter((email) => !isValidEmail(email));
            setErrors((prev) => ({
                ...prev,
                alternateEmail: invalidEmails.length > 0 ? "Email addresses are invalid" : false,
            }));
        }
    };

    // Trigger the onSend callback with email data when Send button is clicked
    const handleSend = () => {
        const { email, alternateEmail, subject, body } = emailData;

        // Validate registered email
        const isRegisteredEmailValid = email.trim() ? isValidEmail(email) : true;

        // Split alternateEmail by commas and validate each email
        const alternateEmails = alternateEmail
            .split(",")
            .map((email) => email.trim())
            .filter(Boolean); // Remove empty strings
        const isAlternateEmailValid = alternateEmails.every((email) => isValidEmail(email));

        // Update error states
        setErrors({
            email: email.trim() && !isRegisteredEmailValid ? "Invalid registered email format" : false,
            alternateEmail: alternateEmails.length > 0 && !isAlternateEmailValid ? "One or more alternate email addresses are invalid" : false,
        });

        // Check if at least one valid email is provided
        if (!email.trim() && alternateEmails.length === 0) {
            toast.error("Please provide at least one valid email address.");
            return;
        }

        if (!isRegisteredEmailValid || (alternateEmails.length > 0 && !isAlternateEmailValid)) {
            toast.error("Please correct the invalid email format(s).");
            return;
        }


        // Prioritize alternate email if provided and valid, otherwise use registered email
        // const selectedEmail = alternateEmail.trim() && isAlternateEmailValid ? alternateEmail : email;
        // if (!selectedEmail) {
        //     toast.error("No valid email provided to send the payment advice.");
        //     return;
        // }


        const selectedEmail = alternateEmail.trim() && isAlternateEmailValid ? alternateEmail : null;

        // Collect valid email addresses into an array
        const toEmails = [];
        if (email.trim() && isRegisteredEmailValid) {
            toEmails.push(email);
        }
        if (alternateEmails.length > 0 && isAlternateEmailValid) {
            toEmails.push(...alternateEmails);
        }

        if (toEmails.length === 0) {
            toast.error("No valid email provided to send the payment advice.");
            return;
        }


        // Call onSend with the email data and ToEmail array
        onSend({ subject, body, toEmails });
    };

    return (
        <Dialog open={open} onClose={onClose}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
        >
            <DialogTitle id="alert-dialog-title">Send Email</DialogTitle>

            {!showContent && (
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Are you sure you want to send Email ?
                    </DialogContentText>
                </DialogContent>
            )}

            {!hideContent && (
                <DialogContent id="alert-dialog-description">
                    <Grid container spacing={0.3}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Registered Email"
                                name="email"
                                value={emailData.email}
                                onChange={handleInputChange}
                                variant="outlined"
                                disabled={!!initialEmail}
                                InputLabelProps={{ shrink: true }}
                                error={!!errors.email}
                                helperText={errors.email}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Alternate Email"
                                name="alternateEmail"
                                value={emailData.alternateEmail}
                                onChange={handleInputChange}
                                variant="outlined"
                                InputLabelProps={{ shrink: true }}
                                error={!!errors.alternateEmail}
                                helperText={errors.alternateEmail || "You can send multiple emails separated by commas"}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Subject"
                                name="subject"
                                value={emailData.subject}
                                onChange={handleInputChange}
                                // variant="outlined"
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Message"
                                name="body"
                                value={emailData.body}
                                onChange={handleInputChange}
                                variant="outlined"
                                multiline
                                rows={4}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
            )}
            <DialogActions>
                <Button
                    color="primary"
                    onClick={handleSend}
                >
                    Yes
                </Button>
                <Button
                    color="primary"
                    onClick={onClose}
                >
                    No
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AdviceLinkPopup;